import { useContext, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { scheduleCrud } from "@/api/schedule";
import type { ScheduleCancellationReason } from "@/api/schedule/models";
import { Button } from "@/components/common/Button";
import { Icon } from "@/components/common/Icon";
import { InputDropdown, InputTextArea } from "@/components/common/inputs";
import { InputReadonly } from "@/components/common/inputs/InputReadonly";
import { ModalAlert } from "@/components/common/modals";
import { SCHEDULE_CANCELLATION_REASON_OPTIONS } from "@/constants/schedule";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { useUserStore } from "@/stores/use-user-store";
import { canCancelSchedule } from "@/utils/features/schedule/permission";
import { getUserRoleByDepartment } from "@/utils/user-positions";

import { ScheduleActionsContext } from "../Provider";

export const ScheduleActionsCancel = () => {
  const { schedule } = useContext(ScheduleActionsContext);
  const { department } = useDepartmentRoute();
  const activeUser = useUserStore((state) => state.activeUser);
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const activeRole = getUserRoleByDepartment(activeUser, department);

  const { action, control, isSubmitting } = useForm({
    initialValues: {
      reason: undefined as ScheduleCancellationReason | undefined,
      reasonDetails: "",
    },
    handleSubmit: async ({ reason, reasonDetails }) => {
      await scheduleCrud.cancel(schedule.id, {
        reason: reason!,
        reasonDetails: reasonDetails || undefined,
      });
      return null;
    },
    fields: { reason: { validation: "required" } },
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["schedule"], refetchType: "all" });
        setIsOpen(false);
        showToast({ type: "success", title: "Atendimento cancelado!" });
      },
      onError: () =>
        showToast({
          type: "error",
          title: "Erro ao cancelar atendimento.",
          content: "Tente novamente em alguns instantes.",
        }),
    },
  });

  if (!canCancelSchedule(activeRole, schedule)) {
    return null;
  }

  return (
    <>
      <Button
        leading={<Icon name="Close" className="text-error" />}
        variant="destructive"
        onClick={() => setIsOpen(true)}
        className="[&]:bg-transparent"
        full
      >
        Cancelar atendimento
      </Button>

      <ModalAlert
        isOpen={isOpen}
        onClose={isSubmitting ? undefined : () => setIsOpen(false)}
        isOverlayCloseable={!isSubmitting}
        cancelable
        title="Tem certeza que deseja cancelar o atendimento do talento?"
        description="Ao confirmar, o talento terá seu agendamento cancelado. Essa ação é irreversível e não poderá ser desfeita."
        actions={[
          {
            type: "submit",
            form: "cancel-schedule-form",
            children: "Cancelar atendimento",
            variant: "destructive",
            isLoading: isSubmitting,
          },
        ]}
      >
        <form className="form-flow" onSubmit={action} id="cancel-schedule-form">
          <InputDropdown
            field="reason"
            label="Motivo do cancelamento"
            options={SCHEDULE_CANCELLATION_REASON_OPTIONS}
            control={control}
          />
          <InputTextArea field="reasonDetails" label="Observações" maxLength={155} control={control} />
          <InputReadonly label="Autor do cancelamento" value={activeUser?.name?.trim() || "Não informado"} />
        </form>
      </ModalAlert>
    </>
  );
};
